import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';
import { X, Plus, GripVertical } from 'lucide-react';
import ImageModal from '../ImageModal';

interface Product {
  id_catalogo: string;
  title?: string;
  sitio?: string;
  sale_price?: string | number;
  original_price?: string | number;
  imagen?: string;
  [key: string]: any;
}

interface SkuProductCardProps {
  product: Product;
  onRemove?: (id_catalogo: string) => void;
  onAdd?: (product: Product) => void;
  isDraggable?: boolean;
  showAddButton?: boolean;
  showRemoveButton?: boolean;
  showDragHandle?: boolean;
  referencePrice?: number | null;
}

const formatPrice = (price: string | number | undefined): string => {
  if (!price) return '-';
  const numPrice = typeof price === 'string' ? parseFloat(price.replace(/[^0-9.-]/g, '')) : price;
  if (isNaN(numPrice)) return '-';
  return '$' + numPrice.toLocaleString('es-CL', { maximumFractionDigits: 0 });
};

const parsePriceNumber = (price: string | number | undefined): number | null => {
  if (!price) return null;
  const numPrice = typeof price === 'string' ? parseFloat(price.replace(/[^0-9.-]/g, '')) : price;
  return isNaN(numPrice) ? null : numPrice;
};

const SkuProductCard: React.FC<SkuProductCardProps> = ({
  product,
  onRemove,
  onAdd,
  isDraggable = false,
  showAddButton = false,
  showRemoveButton = false,
  showDragHandle = false,
  referencePrice,
}) => {
  const { t } = useTranslation();
  const [showImageModal, setShowImageModal] = useState(false);
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: product.id_catalogo,
    data: { product },
    disabled: !isDraggable,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: isDragging ? 1000 : undefined,
      }
    : undefined;

  const getProductUrl = () => {
    return (
      product.url ||
      product.URL ||
      product.Url ||
      product.link ||
      product.enlace ||
      product.product_url ||
      ''
    );
  };

  const getProductTitle = () => product.title || product.titulo || product.nombre || t('skus.no_title');
  const imageUrl = product.imagen || '';
  const productUrl = getProductUrl();

  return (
    <div
      ref={isDraggable ? setNodeRef : undefined}
      style={style}
      {...(isDraggable ? attributes : {})}
      {...(isDraggable ? listeners : {})}
      className={`
        bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3
        ${isDragging ? 'opacity-50 shadow-lg' : 'hover:shadow-md'}
        ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''}
        transition-shadow duration-200
      `}
    >
      {/* Drag handle indicator */}
      {(isDraggable || showDragHandle) && (
        <div className={`flex-shrink-0 ${isDraggable ? 'text-gray-400' : 'text-gray-200'}`}>
          <GripVertical className="h-5 w-5" />
        </div>
      )}

      {/* Imagen */}
      <div
        className="flex-shrink-0 w-14 h-14 bg-gray-100 rounded overflow-hidden cursor-pointer"
        onClick={() => imageUrl && setShowImageModal(true)}
        onPointerDown={(e) => e.stopPropagation()}
        title={t('skus.view_large_image')}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={product.title || 'Producto'}
            className="w-full h-full object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23ccc"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
            </svg>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-gray-900 leading-snug break-words">
          {product.title || t('skus.no_title')}
        </h4>
        <p className="text-xs text-gray-500 leading-snug break-words">
          {product.sitio || t('skus.no_site')}
        </p>
        <div className="mt-1 flex items-baseline gap-2 flex-wrap">
          <span className="text-sm font-semibold text-pink-600">
            {formatPrice(product.sale_price)}
          </span>
          {(() => {
            const sale = parsePriceNumber(product.sale_price);
            const original = parsePriceNumber(product.original_price);
            const hasDiscount = sale !== null && original !== null && sale < original;

            if (!hasDiscount) return null;

            return (
              <span className="text-xs text-gray-400 line-through">
                {formatPrice(product.original_price)}
              </span>
            );
          })()}
        </div>
        {/* Reference price deviation */}
        {(() => {
          if (!referencePrice) return null;
          const salePrice = parsePriceNumber(product.sale_price);
          if (salePrice === null) return null;

          const deviation = salePrice - referencePrice;
          const deviationPercent = ((deviation / referencePrice) * 100).toFixed(0);
          const isPositive = deviation > 0;
          const isNegative = deviation < 0;

          if (deviation === 0) return null;

          const deviationFormatted = formatPrice(Math.abs(deviation));

          return (
            <div className={`mt-1 text-xs font-medium px-2 py-0.5 rounded inline-flex items-center gap-1 ${
              isNegative
                ? 'text-green-700 bg-green-50'
                : 'text-red-700 bg-red-50'
            }`}>
              <span>vs Ref: {isPositive ? '+' : '-'} {deviationFormatted}</span>
              <span className="text-[10px] opacity-75">
                ({isPositive ? '+' : ''}{deviationPercent}%)
              </span>
            </div>
          );
        })()}
      </div>

      {/* Acciones */}
      <div className="flex-shrink-0 flex items-center gap-1">
        {showAddButton && onAdd && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onAdd(product);
            }}
            onPointerDown={(e) => e.stopPropagation()}
            className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-full transition-colors"
            title={t('skus.add_to_sku')}
          >
            <Plus className="h-5 w-5" />
          </button>
        )}
        {showRemoveButton && onRemove && (
          <button
            onClick={() => onRemove(product.id_catalogo)}
            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
            title={t('skus.remove_from_sku')}
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <ImageModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        imageUrl={imageUrl}
        productTitle={getProductTitle()}
        productUrl={productUrl || undefined}
        siteName={product.sitio || product.SITIO || product.Sitio || undefined}
      />
    </div>
  );
};

export default SkuProductCard;
